import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useClienteImpersonation } from "@/contexts/ClienteImpersonationContext";
import type { InvestmentProperty, StageInfo, PaymentRecord, TransactionStage, AdditionalProduct, NotaryData } from "./types";

const IMAGE_GRADIENTS = [
  "from-primary/20 via-primary/10 to-accent",
  "from-success/20 via-success/10 to-accent",
  "from-warning/20 via-warning/10 to-accent",
  "from-purple-500/20 via-purple-500/10 to-accent",
];

const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTHS_LONG = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function fmtMonthYear(dateStr: string): string {
  const d = new Date(dateStr);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtDeliveryDate(dateStr: string | null | undefined, estatusId: number): string {
  if (estatusId === 8) return "Entregada";
  if (!dateStr) return "Por confirmar";
  const d = new Date(dateStr);
  return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtCurrency(amount: number, currency = "MXN"): string {
  return `$${amount.toLocaleString("es-MX")} ${currency}`;
}

function getActiveDescription(stageId: TransactionStage): string {
  switch (stageId) {
    case "preventa": return "Plan de pagos activo";
    case "pago_final": return "Liquidación pendiente";
    case "escrituracion": return "En proceso de escrituración";
    case "entrega": return "Lista para entrega";
    case "post_entrega": return "Propiedad entregada";
  }
}

function buildStages(estatusId: number, pendingBalance: number, deliveryDate: string, hasPendingParcialidades: boolean): StageInfo[] {
  const ALL: { id: TransactionStage; label: string }[] = [
    { id: "preventa", label: "Preventa" },
    { id: "pago_final", label: "Pago Final" },
    { id: "escrituracion", label: "Escrituración" },
    { id: "entrega", label: "Entrega" },
    { id: "post_entrega", label: "Post-Entrega" },
  ];

  // Etapa según avance de pago (Vendida, En demanda, o estatus no mapeado).
  // NO se infiere escrituración/entrega desde el pago: son hitos con estatus
  // propio (7 escrituración, 8 entregado, 9 pagada). Estar liquidado ≠ escriturar
  // (requiere datos notariales). Sin esos estatus, la propiedad está "en pago".
  //   parcialidades pendientes → preventa · resto → pago_final
  const byPaymentProgress = () =>
    hasPendingParcialidades ? 0 : 1;

  // Map estatus_disponibilidad id to the currently active stage index
  let activeIdx: number;
  switch (estatusId) {
    case 10: activeIdx = 0; break; // Asignado → preventa
    case 4: activeIdx = 0; break;  // Apartado → preventa
    // Pagada completamente (9) va ANTES de escriturar en el ciclo de vida:
    // sigue en fase de pago hasta que el estatus avance a Escrituración (7).
    // Liquidar ≠ escriturar; el avance lo dicta el estatus, no el saldo.
    case 9: activeIdx = 1; break;  // Pagada completamente → pago_final (aún sin escriturar)
    case 7: activeIdx = 2; break;  // Escrituración
    case 8: activeIdx = 4; break;  // Entregado → post_entrega
    case 5:                        // Vendida
    case 11:                       // En demanda (legal) - no bloquear el flujo de pago
    default:                       // Cualquier otro estatus: derivar del saldo, no asumir preventa
      activeIdx = byPaymentProgress();
      break;
  }

  // Cap por realidad de pago: escrituración/entrega/post-entrega exigen saldo ≈ 0.
  // Si aún hay saldo pendiente, el estatus_disponibilidad está desfasado (p.ej.
  // reprecio con cuenta nueva que hereda estatus 9 de la anterior). No mostrar una
  // etapa más avanzada que la que corresponde al avance de pago real.
  if (pendingBalance > 0.01) {
    activeIdx = Math.min(activeIdx, hasPendingParcialidades ? 0 : 1);
  }

  return ALL.map((s, i): StageInfo => {
    if (i < activeIdx) {
      return { id: s.id, label: s.label, description: "Completado", status: "completed" };
    }
    if (i === activeIdx) {
      const contextMessages: Partial<Record<TransactionStage, string>> = {
        preventa: "Plan de pagos activo",
        pago_final: `Saldo pendiente: ${fmtCurrency(pendingBalance)}`,
        escrituracion: "En proceso de escrituración",
        entrega: deliveryDate !== "Por confirmar" ? `Lista para entrega · ${deliveryDate}` : "Agenda tu cita de entrega",
        post_entrega: "Propiedad entregada y escriturada",
      };
      const base: StageInfo = {
        id: s.id,
        label: s.label,
        description: getActiveDescription(s.id),
        status: "active",
        contextMessage: contextMessages[s.id],
      };
      if (s.id === "pago_final" && pendingBalance > 0) {
        return {
          ...base,
          cta: { label: "Pagar ahora", action: "balance" },
          details: { "Saldo pendiente": fmtCurrency(pendingBalance) },
        };
      }
      return base;
    }
    return { id: s.id, label: s.label, description: "Pendiente", status: "pending" };
  });
}

type PagoInfo = {
  pagoId?: number;
  trackingKey?: string;
  paymentMethodId?: number;
  fechaPago?: string;
  cepUrl?: string;
  evidenceUrl?: string;
};

async function fetchPortfolio(personaId: number): Promise<InvestmentProperty[]> {
  // Cuentas de cobranza en las que participa la persona, por DOS caminos:
  //  (a) lead de la oferta       → ofertas.id_persona_lead
  //  (b) copropietario/comprador → compradores.id_persona
  // Se unen para que los copropietarios (no solo el lead) vean su unidad.
  const [leadOffersRes, compradorRowsRes] = await Promise.all([
    supabase.from("ofertas").select("id").eq("id_persona_lead", personaId).eq("activo", true),
    supabase.from("compradores").select("id_cuenta_cobranza").eq("id_persona", personaId).eq("activo", true),
  ]);

  const leadOfferIds = (leadOffersRes.data ?? []).map((o) => o.id as number);
  const compradorCuentaIds = [
    ...new Set((compradorRowsRes.data ?? []).map((r) => r.id_cuenta_cobranza as number).filter(Boolean)),
  ];

  // cuentaIds del path lead (via id_oferta) - aprobadas y activas
  const { data: leadCuentasIdRows } = leadOfferIds.length
    ? await supabase
        .from("cuentas_cobranza")
        .select("id")
        .in("id_oferta", leadOfferIds)
        .eq("activo", true)
        .eq("es_aprobado", true)
    : { data: [] as { id: number }[] };

  const allCuentaIds = [
    ...new Set([
      ...(leadCuentasIdRows ?? []).map((c) => c.id as number),
      ...compradorCuentaIds,
    ]),
  ];

  if (!allCuentaIds.length) return [];

  // Datos completos de esas cuentas (incluye clabe_stp para el modal de recibo)
  const { data: cuentasFull } = await supabase
    .from("cuentas_cobranza")
    .select("id, id_oferta, id_propiedad, id_cuenta_cobranza_padre, precio_final, moneda, fecha_compra, fecha_escritura, clabe_stp, id_notario")
    .in("id", allCuentaIds)
    .eq("activo", true)
    .eq("es_aprobado", true);

  if (!cuentasFull?.length) return [];

  // Ofertas de esas cuentas → id_producto (principal vs producto) + id_propiedad de respaldo
  const ofertaIds = [...new Set(cuentasFull.map((c) => c.id_oferta as number).filter(Boolean))];
  const { data: ofertasData } = ofertaIds.length
    ? await supabase.from("ofertas").select("id, id_propiedad, id_producto").in("id", ofertaIds)
    : { data: [] as { id: number; id_propiedad: number | null; id_producto: number | null }[] };

  const ofertaMap: Record<number, { id_propiedad: number | null; id_producto: number | null }> =
    Object.fromEntries(
      (ofertasData ?? []).map((o) => [
        o.id as number,
        { id_propiedad: (o.id_propiedad as number | null) ?? null, id_producto: (o.id_producto as number | null) ?? null },
      ]),
    );

  // Separar cuentas principales (oferta sin producto) de cuentas de producto.
  // Fallback: si la cuenta no trae id_propiedad, tomarlo de la oferta.
  const mainCuentasRaw: Record<string, unknown>[] = [];
  const productCuentas: Record<string, unknown>[] = [];
  for (const c of cuentasFull as Record<string, unknown>[]) {
    // Cuentas HIJAS (mantenimiento/sub-cuentas con padre) no son propiedades
    // por sí mismas; se procesan aparte (paso 3.6). Incluirlas aquí generaba
    // "cards fantasma" sin proyecto/unidad (ej. cuenta sin oferta ni propiedad).
    if (c.id_cuenta_cobranza_padre != null) continue;
    const of = ofertaMap[c.id_oferta as number];
    if (c.id_propiedad == null) c.id_propiedad = of?.id_propiedad ?? null;
    if (of?.id_producto != null) productCuentas.push(c);
    // Cuenta principal sin propiedad resoluble = malformada → no se puede pintar.
    else if (c.id_propiedad != null) mainCuentasRaw.push(c);
  }

  const cuentas = mainCuentasRaw;
  if (!cuentas.length) return [];

  const cuentaIds = cuentas.map((c) => c.id as number);
  const propiedadIds = [...new Set(cuentas.map((c) => c.id_propiedad as number).filter(Boolean))];

  // Fetch tipo_financiamiento per cuenta (graceful - column added via DDL 20260610_08)
  const tipoFinMap: Record<number, 'RECURSOS_PROPIOS' | 'CREDITO_HIPOTECARIO' | null> = {};
  try {
    const { data: tfRows } = await (supabase as any)
      .from('cuentas_cobranza')
      .select('id, tipo_financiamiento')
      .in('id', cuentaIds);
    for (const r of (tfRows ?? [])) {
      tipoFinMap[r.id as number] = r.tipo_financiamiento ?? null;
    }
  } catch (_) {}

  const enrichedCuentas = (cuentas as Record<string, unknown>[]).map(c => ({
    ...(c as Record<string, unknown>),
    tipo_financiamiento: tipoFinMap[c.id as number] ?? null,
  })) as Record<string, unknown>[];

  // 2.5 additionalProducts desde las cuentas de producto (lead o copropietario)
  const productsByPropId: Record<number, AdditionalProduct[]> = {};
  if (productCuentas.length) {
    const productoIds = [
      ...new Set(
        productCuentas.map((c) => ofertaMap[c.id_oferta as number]?.id_producto as number).filter(Boolean),
      ),
    ];
    const productCuentaIds = productCuentas.map((c) => c.id as number);

    const [productosRes, productPagosRes] = await Promise.all([
      productoIds.length
        ? supabase.from("productos_servicios").select("id, nombre, descripcion, id_categoria").in("id", productoIds)
        : Promise.resolve({ data: [] as { id: number; nombre: string; descripcion: string | null; id_categoria: number | null }[] }),
      supabase
        .from("pagos")
        .select("id_cuenta_cobranza, monto")
        .in("id_cuenta_cobranza", productCuentaIds)
        .eq("activo", true),
    ]);

    const productosMap: Record<number, { nombre: string; descripcion: string | null; categoriaId: number | null }> =
      Object.fromEntries(
        (productosRes.data ?? []).map((p) => [
          p.id as number,
          {
            nombre: String(p.nombre),
            descripcion: p.descripcion ? String(p.descripcion) : null,
            categoriaId: (p as any).id_categoria ?? null,
          },
        ]),
      );

    const pagosByCuenta: Record<number, number> = {};
    for (const p of productPagosRes.data ?? []) {
      const cId = p.id_cuenta_cobranza as number;
      pagosByCuenta[cId] = (pagosByCuenta[cId] ?? 0) + Number(p.monto);
    }

    for (const cc of productCuentas) {
      const of = ofertaMap[cc.id_oferta as number];
      const propId = (of?.id_propiedad as number | null) ?? null;
      const productoId = (of?.id_producto as number | null) ?? null;
      if (!propId || !productoId) continue;

      const totalPaid = pagosByCuenta[cc.id as number] ?? 0;
      const totalPrice = Number(cc.precio_final);
      const pendingBalance = Math.max(0, totalPrice - totalPaid);

      let status: AdditionalProduct["status"];
      if (totalPaid === 0) status = "pendiente";
      else if (pendingBalance > 0) status = "financiado";
      else status = "pagado";

      const ps = productosMap[productoId];
      const prod: AdditionalProduct = {
        id: String(cc.id),
        name: ps?.nombre ?? "Producto",
        categoriaId: ps?.categoriaId ?? null,
        description: ps?.descripcion ?? undefined,
        totalPrice,
        totalPaid,
        pendingBalance,
        status,
        documents: [],
      };

      if (!productsByPropId[propId]) productsByPropId[propId] = [];
      productsByPropId[propId].push(prod);
    }
  }

  // 3. Parallel: properties + payments + payment schedule + persona + notarios
  const notarioIds = [...new Set(cuentas.map((c) => (c as Record<string, unknown>).id_notario as number).filter(Boolean))];
  const [propiedadesRes, pagosRes, acuerdosRes, personaRes, notariosRes] = await Promise.all([
    supabase
      .from("propiedades")
      .select("id, numero_propiedad, numero_piso, m2_interiores, precio_lista, id_estatus_disponibilidad, id_edificio_modelo, id_tipo_propiedad, url_imagen_portada")
      .in("id", propiedadIds),
    supabase
      .from("pagos")
      .select("id, id_cuenta_cobranza, monto, fecha_pago, descripcion, url_recibo, url_cep, clave_rastreo, id_metodos_pago")
      .in("id_cuenta_cobranza", cuentaIds)
      .eq("activo", true)
      .order("fecha_pago", { ascending: true }),
    supabase
      .from("acuerdos_pago")
      .select("id, id_cuenta_cobranza, id_concepto, fecha_pago, monto, pago_completado, orden")
      .in("id_cuenta_cobranza", cuentaIds)
      .eq("activo", true)
      .order("orden", { ascending: true }),
    supabase.from("personas").select("nombre_legal, rfc").eq("id", personaId).single(),
    notarioIds.length
      ? (supabase as any).from("notarios").select("id, nombre, notaria, telefono, email, direccion").in("id", notarioIds)
      : Promise.resolve({ data: [] as { id: number; nombre: string; notaria: string; telefono: string; email: string; direccion: string }[] }),
  ]);

  const propiedades = propiedadesRes.data ?? [];
  const pagos = pagosRes.data ?? [];
  const acuerdos = acuerdosRes.data ?? [];
  const persona = personaRes.data;
  const notariosMap: Record<number, NotaryData> = Object.fromEntries(
    ((notariosRes.data as { id: number; nombre: string; notaria: string; telefono: string; email: string; direccion: string }[]) ?? []).map((n) => [
      n.id,
      { name: String(n.nombre ?? ""), notaria: String(n.notaria ?? ""), phone: String(n.telefono ?? ""), email: String(n.email ?? ""), address: String(n.direccion ?? "") },
    ]),
  );

  // 3.5 Link acuerdos_pago → pagos to get tracking info
  const acuerdoIds = acuerdos.map((a) => a.id as number);
  const { data: aplicaciones } = acuerdoIds.length
    ? await supabase
        .from("aplicaciones_pago")
        .select("id_acuerdo_pago, id_pago")
        .in("id_acuerdo_pago", acuerdoIds)
        .eq("activo", true)
    : { data: [] as { id_acuerdo_pago: number; id_pago: number }[] };

  const pagoById: Record<number, Record<string, unknown>> = Object.fromEntries(
    pagos.map((p) => [p.id as number, p]),
  );
  const pagoInfoPorAcuerdo: Record<number, PagoInfo> = {};
  for (const ap of (aplicaciones ?? [])) {
    const pago = pagoById[ap.id_pago as number];
    if (pago) {
      pagoInfoPorAcuerdo[ap.id_acuerdo_pago as number] = {
        pagoId: Number(pago.id),
        trackingKey: pago.clave_rastreo ? String(pago.clave_rastreo) : undefined,
        paymentMethodId: pago.id_metodos_pago ? Number(pago.id_metodos_pago) : undefined,
        fechaPago: pago.fecha_pago ? String(pago.fecha_pago).slice(0, 10) : undefined,
        cepUrl: pago.url_cep ? String(pago.url_cep) : undefined,
        evidenceUrl: pago.url_recibo ? String(pago.url_recibo) : undefined,
      };
    }
  }

  // Fetch metodos_pago names
  const metodosIds = [...new Set(pagos.map((p) => p.id_metodos_pago as number).filter(Boolean))];
  const { data: metodosData } = metodosIds.length
    ? await supabase.from("metodos_pago").select("id, nombre").in("id", metodosIds)
    : { data: [] };
  const metodosMap: Record<number, string> = Object.fromEntries(
    (metodosData ?? []).map((m) => [m.id as number, String(m.nombre)]),
  );

  // 3.6 Fetch maintenance child cuentas and their acuerdos (patrimony properties)
  const mantoAcuerdosByParentId: Record<number, { fecha_pago: string; monto: number; pago_completado: boolean }[]> = {};
  {
    const { data: childCuentas } = await supabase
      .from("cuentas_cobranza")
      .select("id, id_cuenta_cobranza_padre")
      .in("id_cuenta_cobranza_padre", cuentaIds)
      .eq("activo", true);

    if (childCuentas?.length) {
      const childIds = childCuentas.map((c) => c.id as number);
      const { data: childAcuerdos } = await supabase
        .from("acuerdos_pago")
        .select("id_cuenta_cobranza, fecha_pago, monto, pago_completado")
        .in("id_cuenta_cobranza", childIds)
        .eq("activo", true)
        .order("fecha_pago", { ascending: true });

      const childToParent: Record<number, number> = {};
      for (const cc of childCuentas) {
        childToParent[cc.id as number] = cc.id_cuenta_cobranza_padre as number;
      }
      for (const a of (childAcuerdos ?? [])) {
        const parentId = childToParent[a.id_cuenta_cobranza as number];
        if (!parentId) continue;
        if (!mantoAcuerdosByParentId[parentId]) mantoAcuerdosByParentId[parentId] = [];
        mantoAcuerdosByParentId[parentId].push({
          fecha_pago: a.fecha_pago ? String(a.fecha_pago).slice(0, 10) : "",
          monto: Number(a.monto),
          pago_completado: Boolean(a.pago_completado),
        });
      }
    }
  }

  // 4. Get edificios_modelos for project chain
  const edificioModeloIds = [...new Set(propiedades.map((p) => p.id_edificio_modelo as number).filter(Boolean))];
  if (!edificioModeloIds.length) return buildFromData(enrichedCuentas, propiedades, [], [], [], [], [], pagos, acuerdos, pagoInfoPorAcuerdo, metodosMap, persona, productsByPropId, notariosMap, mantoAcuerdosByParentId);

  const { data: edificiosModelos } = await supabase
    .from("edificios_modelos")
    .select("id, id_edificio, id_modelo")
    .in("id", edificioModeloIds);

  const edificioIds = [...new Set((edificiosModelos ?? []).map((em) => em.id_edificio as number).filter(Boolean))];
  const modeloIds = [...new Set((edificiosModelos ?? []).map((em) => em.id_modelo as number).filter(Boolean))];
  const tipoIds = [...new Set(propiedades.map((p) => p.id_tipo_propiedad as number).filter(Boolean))];

  // 5. Parallel: edificios + modelos + tipos_propiedad
  const [edificiosRes, modelosRes, tiposRes] = await Promise.all([
    edificioIds.length
      ? supabase.from("edificios").select("id, id_proyecto, nombre").in("id", edificioIds)
      : Promise.resolve({ data: [] }),
    modeloIds.length
      ? supabase.from("modelos").select("id, numero_recamaras, numero_completo_banos, numero_medio_bano").in("id", modeloIds)
      : Promise.resolve({ data: [] }),
    tipoIds.length
      ? supabase.from("tipos_propiedad").select("id, nombre").in("id", tipoIds)
      : Promise.resolve({ data: [] }),
  ]);

  const edificios = edificiosRes.data ?? [];
  const proyectoIds = [...new Set(edificios.map((e) => e.id_proyecto as number).filter(Boolean))];

  const { data: proyectos } = proyectoIds.length
    ? await supabase
        .from("proyectos")
        .select("id, nombre, direccion, precio_m2_actual, fecha_entrega_proyecto, costo_mantenimiento_m2, url_imagen_portada")
        .in("id", proyectoIds)
    : { data: [] };

  return buildFromData(
    enrichedCuentas,
    propiedades,
    edificiosModelos ?? [],
    edificios,
    modelosRes.data ?? [],
    tiposRes.data ?? [],
    proyectos ?? [],
    pagos,
    acuerdos,
    pagoInfoPorAcuerdo,
    metodosMap,
    persona,
    productsByPropId,
    notariosMap,
    mantoAcuerdosByParentId,
  );
}

const CONCEPTO_NAMES: Record<number, string> = {
  1: "Apartado",
  2: "Enganche",
  3: "Pago a escrituración",
  4: "Mensualidad",
  5: "Parcialidad",
};

function buildFromData(
  cuentas: Record<string, unknown>[],
  propiedades: Record<string, unknown>[],
  edificiosModelos: Record<string, unknown>[],
  edificios: Record<string, unknown>[],
  modelos: Record<string, unknown>[],
  tipos: Record<string, unknown>[],
  proyectos: Record<string, unknown>[],
  pagos: Record<string, unknown>[],
  acuerdos: Record<string, unknown>[],
  pagoInfoPorAcuerdo: Record<number, PagoInfo> = {},
  metodosMap: Record<number, string> = {},
  persona: { nombre_legal: string | null; rfc: string | null } | null = null,
  productsByPropId: Record<number, AdditionalProduct[]> = {},
  notariosMap: Record<number, NotaryData> = {},
  mantoAcuerdosByParentId: Record<number, { fecha_pago: string; monto: number; pago_completado: boolean }[]> = {},
): InvestmentProperty[] {
  const propMap = Object.fromEntries(propiedades.map((p) => [p.id as number, p]));
  const emMap = Object.fromEntries(edificiosModelos.map((em) => [em.id as number, em]));
  const edificioMap = Object.fromEntries(edificios.map((e) => [e.id as number, e]));
  const modeloMap = Object.fromEntries(modelos.map((m) => [m.id as number, m]));
  const tipoMap = Object.fromEntries(tipos.map((t) => [t.id as number, t]));
  const proyectoMap = Object.fromEntries(proyectos.map((p) => [p.id as number, p]));

  return cuentas.map((cc, idx): InvestmentProperty => {
    const prop = propMap[cc.id_propiedad as number] ?? null;
    const em = prop ? emMap[prop.id_edificio_modelo as number] : null;
    const edificio = em ? edificioMap[em.id_edificio as number] : null;
    const proyecto = edificio ? proyectoMap[edificio.id_proyecto as number] : null;
    const modelo = em ? modeloMap[em.id_modelo as number] : null;
    const tipo = prop ? tipoMap[prop.id_tipo_propiedad as number] : null;

    const cuentaPagos = pagos.filter((p) => p.id_cuenta_cobranza === cc.id);
    const totalPaid = cuentaPagos.reduce((sum, p) => sum + Number(p.monto), 0);
    // Cuentas legacy/incompletas con precio_final=0 → usar precio_lista de la propiedad
    const precioFinal = Number(cc.precio_final);
    const initialPrice = precioFinal > 0 ? precioFinal : Number(prop?.precio_lista ?? 0);
    const pendingBalance = Math.max(0, initialPrice - totalPaid);

    const m2 = Number(prop?.m2_interiores ?? 0);
    const pricePerM2Initial = m2 > 0 ? initialPrice / m2 : 0;
    const pricePerM2Current = Number(proyecto?.precio_m2_actual ?? 0);
    const currentEstimatedValue = m2 > 0 && pricePerM2Current > 0 ? m2 * pricePerM2Current : initialPrice;
    const estimatedAppreciation =
      pricePerM2Initial > 0 && pricePerM2Current > 0
        ? Math.round(((pricePerM2Current - pricePerM2Initial) / pricePerM2Initial) * 1000) / 10
        : 0;

    const estatusId = Number(prop?.id_estatus_disponibilidad ?? 4);
    const maintenanceFee = proyecto && m2 > 0
      ? Math.round(Number(proyecto.costo_mantenimiento_m2) * m2)
      : 0;

    const deliveryDate = fmtDeliveryDate(proyecto?.fecha_entrega_proyecto as string | null, estatusId);

    // Build payment schedule from acuerdos_pago (includes pending items)
    const cuentaAcuerdos = acuerdos
      .filter(a => a.id_cuenta_cobranza === cc.id)
      .sort((a, b) => Number(a.orden) - Number(b.orden));

    // Conceptos 4 (Mensualidad) y 5 (Parcialidad) son parcialidades durante obra
    const hasPendingParcialidades = cuentaAcuerdos.some(
      a => !a.pago_completado && [4, 5].includes(Number(a.id_concepto))
    );

    let parcialidadCount = 0;
    const paymentRecords: PaymentRecord[] = cuentaAcuerdos.map(a => {
      const idConcepto = Number(a.id_concepto);
      let concept = CONCEPTO_NAMES[idConcepto] ?? "Pago";
      if (idConcepto === 5) { parcialidadCount++; concept = `Parcialidad ${parcialidadCount}`; }
      const info = pagoInfoPorAcuerdo[a.id as number];
      // fecha_pago del acuerdo puede venir null aun estando pagado (la fecha real
      // vive en el pago). Fallback al pago; si no hay, cadena vacía (nunca "null").
      const fechaAcuerdo = a.fecha_pago ?? info?.fechaPago ?? null;
      return {
        date: fechaAcuerdo ? String(fechaAcuerdo).slice(0, 10) : "",
        concept,
        amount: Number(a.monto),
        status: (a.pago_completado ? "pagado" : "pendiente") as "pagado" | "pendiente",
        pagoId: info?.pagoId,
        cepUrl: info?.cepUrl,
        evidenceUrl: info?.evidenceUrl,
        trackingKey: info?.trackingKey,
        paymentMethodName: info?.paymentMethodId ? metodosMap[info.paymentMethodId] : undefined,
      };
    });

    return {
      property: {
        id: String(cc.id),
        projectName: String(proyecto?.nombre ?? "-"),
        unitNumber: String(prop?.numero_propiedad ?? "-"),
        location: String(proyecto?.direccion ?? ""),
        type: String(tipo?.nombre ?? "Propiedad"),
        area: m2 > 0 ? `${m2.toFixed(1)} m²` : "-",
        floor: String(prop?.numero_piso ?? "-"),
        bedrooms: Number(modelo?.numero_recamaras ?? 0),
        bathrooms:
          Number(modelo?.numero_completo_banos ?? 0) +
          Number(modelo?.numero_medio_bano ?? 0),
        deliveryDate,
        imageGradient: IMAGE_GRADIENTS[idx % IMAGE_GRADIENTS.length],
        image: proyecto?.url_imagen_portada ? String(proyecto.url_imagen_portada) : undefined,
        fechaEscritura: cc.fecha_escritura ? String(cc.fecha_escritura) : undefined,
        projectId: edificio?.id_proyecto as number | undefined,
        idPropiedad: cc.id_propiedad as number | undefined,
        clientName: persona?.nombre_legal ?? undefined,
        clientRFC: persona?.rfc ?? undefined,
        notary: cc.id_notario ? notariosMap[cc.id_notario as number] : undefined,
        tipoFinanciamiento: cc.tipo_financiamiento
          ? (String(cc.tipo_financiamiento) as 'RECURSOS_PROPIOS' | 'CREDITO_HIPOTECARIO' | 'FINANCIAMIENTO_EXTERNO')
          : null,
        enDemanda: estatusId === 11,
      },
      financials: {
        initialPrice,
        totalPaid,
        pendingBalance,
        estimatedAppreciation,
        currentEstimatedValue,
        pricePerM2Initial,
        pricePerM2Current,
        currency: String(cc.moneda ?? "MXN"),
        clabe: cc.clabe_stp ? String(cc.clabe_stp) : undefined,
      },
      stages: buildStages(estatusId, pendingBalance, deliveryDate, hasPendingParcialidades),
      payments: paymentRecords,
      maintenance: (() => {
        if (estatusId !== 8) return undefined;
        const mantoAcuerdos = mantoAcuerdosByParentId[cc.id as number] ?? [];
        const pendingManto = mantoAcuerdos
          .filter((a) => !a.pago_completado)
          .sort((a, b) => a.fecha_pago.localeCompare(b.fecha_pago));
        const nextManto = pendingManto[0];
        if (!mantoAcuerdos.length && maintenanceFee <= 0) return undefined;
        return {
          monthlyFee: nextManto ? nextManto.monto : maintenanceFee,
          nextDueDate: nextManto ? nextManto.fecha_pago : "",
          status: (pendingManto.length > 0 ? "pendiente" : "pagado") as "pagado" | "pendiente",
          history: [],
        };
      })(),
      additionalProducts: productsByPropId[cc.id_propiedad as number] ?? [],
    };
  });
}

export function usePortfolioCliente() {
  const { profile } = useAuth();
  const { isImpersonating, impersonatedClientePersonaId } = useClienteImpersonation();

  const personaId: number | null = isImpersonating
    ? impersonatedClientePersonaId
    : profile?.rol_nombre === "Cliente"
    ? (profile?.id_persona ?? null)
    : null;

  return useQuery({
    queryKey: ["portfolio-cliente", personaId],
    queryFn: () => fetchPortfolio(personaId!),
    enabled: personaId != null,
    staleTime: 5 * 60 * 1000,
  });
}
